import type { BindFeature } from '@wix/thunderbolt-catharsis-ioc'
import { appConstant } from '@wix/thunderbolt-catharsis-ioc'
import { getCatharsisMappers } from '@wix/thunderbolt-components'
import type { NativeExtension, NativeExtensions } from '@wix/thunderbolt-components-native'
import { withDependencies } from '@wix/thunderbolt-ioc'
import { CssFeatureSymbol } from '../../ioc/catharsis-ioc'
import { compStyles, compDesigns } from '../styleBased/compStylesCompNode'
import { themeVariants } from '../styleBased/themeVariantsCompNode'
import type { CompCssMappersFeature } from './compCssMappers.types'
import { compCssMappersDomApplier } from './compCssMappersDomApplier'
import { childDataCompNode, compCssMappersWithModesAndVariants } from './compCssMappersWithModesAndVariantsCompNode'
import { compDesignCssMappers } from './compDesignCssMappersCompNode'
import { compSelectorToCssMappers } from './compSelectorToCssMappersCompNode'
import { cssCarmiMappers } from './cssCarmiMappers'
import { designVariants } from './designVariantsCompNode'
import { isStylable } from './isStylableCompNode'
import { uiTypeCompNode } from './uiTypeCompNode'
import { compInfoNodes } from '@wix/thunderbolt-component-isolation'
import type { BeckyModel, ComponentsNativeMappers } from '@wix/thunderbolt-becky-root'
import type { RunAndReportFn } from '@wix/thunderbolt-state-manager'
import { createCompNode } from '@wix/thunderbolt-catharsis'

export const compCssMappersFeatureWrapper = (isInEditor: boolean) => (
	componentsMappers: ComponentsNativeMappers,
	isExperimentOpen: (experimentName: keyof BeckyModel['experiments']) => boolean,
	runAndReport: RunAndReportFn = (fn: any) => fn()
): CompCssMappersFeature => {
	const catharsisMappers = getCatharsisMappers(isExperimentOpen)
	const extensionsPerType = Object.entries(componentsMappers)
		.filter(
			([componentType]) => catharsisMappers.hasOwnProperty(componentType) || !cssCarmiMappers.has(componentType)
		)
		.reduce(
			(acc, [componentType, { css, selectorToCss, uiType, designCss }]) => {
				if (css) {
					acc.css[componentType] = css as NativeExtension
				}
				if (selectorToCss) {
					acc.selectorToCss[componentType] = selectorToCss as NativeExtension
				}
				if (uiType) {
					acc.uiType[componentType] = uiType as NativeExtension
				}
				if (designCss) {
					acc.designCss[componentType] = designCss as NativeExtension
				}
				return acc
			},
			{
				css: {} as NativeExtensions['extensions'],
				selectorToCss: {} as NativeExtensions['extensions'],
				uiType: {} as NativeExtensions['extensions'],
				designCss: {} as NativeExtensions['extensions'],
			}
		)

	return {
		experimentName: 'OPEN_TO_ALL',
		renderableNodes: {
			themeVariants,
			isStylable,
			isInEditor: createCompNode({
				getDependencies: () => isInEditor,
				toViewItem: () => isInEditor,
			}),
			isNestingSelectorsEnabled: createCompNode({
				getDependencies: () => isExperimentOpen('specs.thunderbolt.css_nesting_selectors'),
				toViewItem: () => isExperimentOpen('specs.thunderbolt.css_nesting_selectors'),
			}),
			designVariants,
			compCssMappers: compCssMappersWithModesAndVariants(
				{
					name: 'css',
					extensions: extensionsPerType.css,
				},
				'compCssMappers',
				runAndReport
			),
			compDesignCssMappers: compDesignCssMappers({
				// TODO - Add 'designCss' to NativeExtensionType in EE
				// @ts-ignore
				name: 'designCss',
				extensions: extensionsPerType.designCss,
			}),
			compSelectorToCssMappers: compSelectorToCssMappers(
				{
					name: 'selectorToCss',
					extensions: extensionsPerType.selectorToCss,
				},
				runAndReport
			),
		},
		intermediateNodes: {
			compStyles,
			// @ts-expect-error
			compDesigns,
			// @ts-expect-error
			childDataCompNode,
			uiType: uiTypeCompNode({
				name: 'uiType',
				extensions: extensionsPerType.uiType,
			}),
			hasResponsiveLayout: compInfoNodes.hasResponsiveLayout,
			siteVariables: compInfoNodes.siteVariables,
		},
		dataMaps: [
			'document_data',
			'component_properties',
			'design_data',
			'theme_data',
			'behaviors_data',
			'connections_data',
			'layout_data',
		],
		domApplier: compCssMappersDomApplier,
		featureName: 'compCssMappers',
	}
}

const compCssMappersFeature = withDependencies(
	[appConstant('componentsMappers'), appConstant('isExperimentOpen')] as const,
	compCssMappersFeatureWrapper(false)
)

const editorCompCssMappersFeature = withDependencies(
	[appConstant('componentsMappers'), appConstant('isExperimentOpen')] as const,
	compCssMappersFeatureWrapper(true)
)

export const page: BindFeature = (bind) => {
	bind(CssFeatureSymbol).to(compCssMappersFeature)
}

export const editor: BindFeature = (bind) => {
	bind(CssFeatureSymbol).to(editorCompCssMappersFeature)
}
